/**
 * Portion groups — pure helpers behind the Customers "Grades" dialog's
 * multi-select grouping (collapse several grades into ONE order column with
 * ONE portion tier) and the k-8 band retirement.
 *
 * Browser-safe and dependency-free beyond sibling constants modules: the
 * Customers client, the customers server, and @ordo/schema's derived
 * age-group sync all import EXACTLY these helpers so a group labels and
 * defaults identically everywhere.
 */

import { gradeRangeCodeForGradeLevel } from './grade-matcher'
import { normalizeGradeRangeCode } from './menu-grade-range-gate'
import { buildGradeRangeSpanLabel, menuGradeRangeSortIndex } from './menu-template-title'

/**
 * Grade-range codes retired from NEW selections (soft-retired: the seeded
 * `GradeRange` rows stay for FK integrity + legacy display, but no picker —
 * menu template Grades chips, portion-size dropdowns, the group-tier picker —
 * may offer them).
 *
 * History: `k-8` was retired 2026-07-19 as redundant and UN-retired
 * 2026-08-06. On 2026-09-15 (Sheila) the decision flipped the other way:
 * "Change all K-5 and 6-8 splits to K-8 universally at all locations. Hide
 * K-5 and 6-8 moving forward." The separate K-5 / 6-8 bands caused constant
 * problems (two order columns, two portion tiers, forked shipments), so every
 * school now operates the combined K-8 band with ONE portion tier. PK3-5 and
 * 9-12 are untouched. The migration script that repoints prod rows is
 * `packages/schema/scripts/2026-09-15-collapse-k5-68-into-k8.ts`.
 */
export const RETIRED_GRADE_RANGE_CODES: readonly string[] = Object.freeze(['k-5', '6-8'])

export function isRetiredGradeRangeCode(code: string | null | undefined): boolean {
  return code != null && RETIRED_GRADE_RANGE_CODES.includes(normalizeGradeRangeCode(code))
}

/**
 * The band a retired code collapses INTO. Every writer that receives a
 * retired code (a legacy template tag, a stale picker value, a saved org
 * grade list) remaps through this so new rows never land on a retired band.
 */
export const RETIRED_GRADE_RANGE_SUCCESSORS: Readonly<Record<string, string>> = Object.freeze({
  'k-5': 'k-8',
  '6-8': 'k-8',
})

/**
 * The live band a code resolves to: retired codes map to their successor,
 * everything else is returned normalized. Pure.
 */
export function successorGradeRangeCode(code: string): string {
  const normalized = normalizeGradeRangeCode(code)
  return RETIRED_GRADE_RANGE_SUCCESSORS[normalized] ?? normalized
}

/**
 * Collapse retired codes in a code list onto their successors: each retired
 * code is replaced IN PLACE by its successor, the result is deduped
 * (case/underscore-insensitive) and the original order is otherwise kept —
 * `['pk3-5', 'k-5', '6-8', '9-12']` → `['pk3-5', 'k-8', '9-12']`. Non-retired
 * codes are returned verbatim (unnormalized), so a list with nothing retired
 * comes back member-for-member equal and callers can compare to decide
 * whether a write is needed. Pure.
 */
export function collapseRetiredGradeRangeCodes(codes: readonly string[]): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const raw of codes) {
    if (raw.trim() === '') {
      continue
    }
    const next = isRetiredGradeRangeCode(raw) ? successorGradeRangeCode(raw) : raw
    const key = normalizeGradeRangeCode(next)
    if (!seen.has(key)) {
      seen.add(key)
      out.push(next)
    }
  }
  return out
}

/**
 * USDA meal-pattern bands with DISTINCT federal quantity requirements for
 * NSLP lunch / SBP breakfast. Semantically K-5 and 6-8 ARE separate federal
 * patterns — this constant stays correct even though the platform no longer
 * splits schools on that boundary (see RETIRED_GRADE_RANGE_CODES): every
 * campus operates the USDA K-8 OVERLAP pattern (one menu served across K-5
 * and 6-8 using the stricter intersection), which is a legitimate federal
 * pattern of its own. (pk3-5 has its own preschool pattern but grouping
 * decisions there are product-approved to collapse freely; Sheila scoped the
 * guardrail to the K-12 patterns.)
 */
export const USDA_MEAL_PATTERN_BAND_CODES: readonly string[] = Object.freeze(['k-5', '6-8', '9-12'])

/**
 * The K-12 pattern bands a portion group is measured against for the span
 * WARNING: the combined K-8 overlap pattern and 9-12. A K-8 group is one
 * pattern, not a boundary crossing; only a group that also reaches high
 * school (K-12, 6-12, …) spans a boundary.
 */
export const OPERATING_PATTERN_BAND_CODES: readonly string[] = Object.freeze(['k-8', '9-12'])

/**
 * The distinct canonical band codes a set of free-form grade values
 * naturally matches (via the shared name matcher), in canonical
 * youngest-to-oldest order. Unparseable values are skipped.
 */
export function naturalBandCodesForGrades(gradeValues: readonly string[]): string[] {
  const codes = new Set<string>()
  for (const value of gradeValues) {
    const code = gradeRangeCodeForGradeLevel(value)
    if (code) {
      codes.add(code)
    }
  }
  // Combined bands (k-8) sort at their first constituent's slot so a
  // PK + K-8 + 9-12 cohort orders youngest-to-oldest.
  return Array.from(codes).sort((left, right) => menuGradeRangeSortIndex(left) - menuGradeRangeSortIndex(right))
}

/**
 * Column label for a portion group, from its member grade values: the span
 * collapse of the bands those grades naturally belong to ("PK-12", "K-8").
 * Null when no member grade parses — callers fall back to the tier band's
 * own name.
 */
export function buildPortionGroupLabel(gradeValues: readonly string[]): string | null {
  return buildGradeRangeSpanLabel(naturalBandCodesForGrades(gradeValues))
}

/**
 * Default portion tier for a new group: the seeded band exactly matching the
 * group's span — i.e. every member grade naturally belongs to ONE live band,
 * which is offered. Since 2026-09-15 every K-8 grade matches the combined
 * k-8 band (the matcher no longer emits k-5 / 6-8), so any group of K-8
 * grades defaults to k-8; legacy k-5 / 6-8 codes fold into k-8 the same
 * way. Anything wider (PK-12, K-12, …) has no seeded band, so the user
 * explicitly picks which band's portions the group eats. Returns the band
 * code, or null when the user must pick.
 */
export function defaultPortionGroupTierCode(gradeValues: readonly string[]): string | null {
  const codes = Array.from(new Set(naturalBandCodesForGrades(gradeValues).map((code) => successorGradeRangeCode(code))))
  const only = codes.length === 1 ? codes[0] : undefined
  return only && !isRetiredGradeRangeCode(only) ? only : null
}

/**
 * The distinct K-12 OPERATING pattern bands a group's grades span (K-8 and
 * 9-12 — see OPERATING_PATTERN_BAND_CODES). Length > 1 = the group crosses
 * a federal pattern boundary — warn (never block) when the school
 * participates in NSLP/SBP; private/non-program schools collapse freely.
 * Legacy k-5 / 6-8 matches fold into k-8 first, so a K-8 group never warns.
 */
export function spannedUsdaPatternBands(gradeValues: readonly string[]): string[] {
  const spanned = new Set(naturalBandCodesForGrades(gradeValues).map((code) => successorGradeRangeCode(code)))
  return OPERATING_PATTERN_BAND_CODES.filter((code) => spanned.has(code))
}

/** Programs whose meal patterns make grade-group spans worth a warning. */
const PATTERN_BOUND_PROGRAMS = ['NSLP', 'SBP'] as const

/**
 * Non-blocking warning copy for a portion group, or null when no warning
 * applies. `programs` = the organization's federal program opt-ins
 * (`Organization.programs`); schools on neither NSLP nor SBP never warn.
 */
export function portionGroupPatternWarning(
  gradeValues: readonly string[],
  programs: readonly string[] | null | undefined,
): string | null {
  const participating = (programs ?? []).some((program) =>
    (PATTERN_BOUND_PROGRAMS as readonly string[]).includes(program.trim().toUpperCase()),
  )
  if (!participating) {
    return null
  }
  const spanned = spannedUsdaPatternBands(gradeValues)
  if (spanned.length <= 1) {
    return null
  }
  const bands = spanned.map((code) => code.toUpperCase()).join(', ')
  return (
    `This group spans USDA meal-pattern boundaries (${bands} are distinct federal patterns). ` +
    'One shared portion size may not satisfy every pattern — confirm with the program sponsor.'
  )
}

// ---------------------------------------------------------------------------
// Locked ORDERING portion bands (2026-08-22, Sheila): the school order sheet
// orders in USDA portion bands derived from the MENU's grade tags — never
// from campus column configuration. The PARTITION (which band columns exist)
// is keyed by the org's PROGRAM and the MEALTIME; the defaults below are the
// seed rules the menus-app "Grade mapping" admin surface (Menus ->
// Maintenance) overrides per program (Private | NSLP | SBP | CACFP | SFSP |
// SSO) — every consumer resolves through `resolveOrderBandPartition` /
// `orderBandPartition`, nothing else.
//
// Defaults (2026-10-21, Sheila — each mealtime is its own setting; the old
// Lunch/Supper and Breakfast/Snack pairs are gone):
//   NSLP / SBP / SFSP / SSO —
//     Breakfast:  PK | K-12   (merged; PK stays separate)
//     Lunch:      PK | K-8 | 9-12
//     Snack:      single column
//     Supper:     single column
//   CACFP — PK | K-12 (merged) at every mealtime.
//   Private (no federal program) — ONE band-less column per mealtime; PK
//     folds in (no pattern boundary to respect).
//
// Menu-tag folding (these are the portion sizes by definition — the same
// partition the nutritional-analysis chips lock to):
//   age-1-2 / age-3-5 (pk3-5)  →  PK
//   k-5 / 6-8 (legacy) / k-8   →  K-8   (→ K-12 where the partition collapses)
//   9-12                       →  9-12  (→ K-12 where the partition collapses)
// ---------------------------------------------------------------------------

export const ORDER_PORTION_BAND_CODES = Object.freeze(['infant', 'age-1-5', 'pk', 'k-8', '9-12', 'k-12'] as const)
export type OrderPortionBandCode = (typeof ORDER_PORTION_BAND_CODES)[number]

// ---------------------------------------------------------------------------
// Audiences (2026-10-22, Sheila): the top-level band above Mealtime ›
// Program › Menu Line. CACFP at a childcare center is a different world from
// CACFP at a K-12 school, so the grade mapping (and Menu Lines) key on the
// audience first. A school's audience comes from its Customers school type.
// ---------------------------------------------------------------------------

export const AUDIENCES = Object.freeze(['K12', 'CHILDCARE', 'ADULT'] as const)
export type Audience = (typeof AUDIENCES)[number]

export const AUDIENCE_LABELS: Readonly<Record<Audience, string>> = Object.freeze({
  K12: 'K-12 Schools',
  CHILDCARE: 'Childcare & Preschool',
  ADULT: 'Adult Programs',
})

export function isAudience(value: unknown): value is Audience {
  return typeof value === 'string' && (AUDIENCES as readonly string[]).includes(value)
}

/**
 * A school's audience from its Customers school type (`school.type` org
 * setting): PRESCHOOL is childcare, ADULT is an adult program, everything
 * else (DISTRICT, CHARTER, PRIVATE, unset) is a K-12 school.
 */
export function audienceForSchoolType(schoolType: string | null | undefined): Audience {
  switch ((schoolType ?? '').trim().toUpperCase()) {
    case 'PRESCHOOL':
      return 'CHILDCARE'
    case 'ADULT':
      return 'ADULT'
    default:
      return 'K12'
  }
}

/** The program vocabulary the band partition is keyed by. */
export const ORDER_BAND_PROGRAMS = Object.freeze(['PRIVATE', 'NSLP', 'SBP', 'CACFP', 'SFSP', 'SSO'] as const)

/** Federal programs whose meal patterns bind the order-sheet partition. */
export const ORDER_PATTERN_BOUND_PROGRAMS: readonly string[] = Object.freeze(['NSLP', 'SBP', 'CACFP', 'SFSP', 'SSO'])

/** True when the org's program opt-ins bind it to the federal partitions. */
export function isOrderPatternBound(programs: readonly string[] | null | undefined): boolean {
  return (programs ?? []).some((program) => ORDER_PATTERN_BOUND_PROGRAMS.includes(program.trim().toUpperCase()))
}

/**
 * The order-sheet partition for one mealtime:
 *   - `SINGLE` — one band-less column, everything (PK included) folds in.
 *   - `BANDS`  — one column per listed band, canonical order.
 */
export type OrderBandPartition = { kind: 'SINGLE' } | { kind: 'BANDS'; bands: readonly OrderPortionBandCode[] }

/**
 * The Grade Mapping mealtime settings, in display order. Each is configured
 * on its own (AM_SNACK / PM_SNACK read the Snack setting; DINNER reads
 * Supper).
 */
export const ORDER_BAND_MEALTIME_STYLES = Object.freeze(['breakfast', 'lunch', 'snack', 'supper'] as const)
export type OrderBandMealtimeStyle = (typeof ORDER_BAND_MEALTIME_STYLES)[number]

/**
 * Which Grade Mapping setting a mealtime reads. Unknown / missing mealtimes
 * read Breakfast — the merged partition, so a stray mealtime never splits
 * columns a school did not ask for.
 */
export function orderBandStyleForMealtime(mealtime: string | null | undefined): OrderBandMealtimeStyle {
  switch ((mealtime ?? '').trim().toUpperCase()) {
    case 'LUNCH':
      return 'lunch'
    case 'SNACK':
    case 'AM_SNACK':
    case 'PM_SNACK':
      return 'snack'
    case 'SUPPER':
    case 'DINNER':
      return 'supper'
    default:
      return 'breakfast'
  }
}

const MERGED_BANDS: readonly OrderPortionBandCode[] = Object.freeze(['pk', 'k-12'])
const SPLIT_BANDS: readonly OrderPortionBandCode[] = Object.freeze(['pk', 'k-8', '9-12'])
/** Childcare & Preschool: Infants | Age 1–5 at every mealtime, whatever the program. */
const CHILDCARE_BANDS: readonly OrderPortionBandCode[] = Object.freeze(['infant', 'age-1-5'])

/**
 * Ordo's shipped band list for one program at one mealtime setting; an
 * EMPTY list means the single band-less column. Pure.
 *   - non-pattern program (PRIVATE, unknown) → single column everywhere
 *   - CACFP → PK | K-12 at every mealtime
 *   - NSLP / SBP / SFSP / SSO → Breakfast PK | K-12, Lunch PK | K-8 | 9-12,
 *     Snack and Supper single column
 */
export function defaultOrderBandsForProgram(
  program: string | null | undefined,
  style: OrderBandMealtimeStyle,
  audience: Audience = 'K12',
): OrderPortionBandCode[] {
  if (audience === 'CHILDCARE') {
    return [...CHILDCARE_BANDS]
  }
  if (audience === 'ADULT') {
    return []
  }
  const normalized = (program ?? '').trim().toUpperCase()
  if (!ORDER_PATTERN_BOUND_PROGRAMS.includes(normalized)) {
    return []
  }
  if (normalized === 'CACFP') {
    return [...MERGED_BANDS]
  }
  switch (style) {
    case 'breakfast':
      return [...MERGED_BANDS]
    case 'lunch':
      return [...SPLIT_BANDS]
    default:
      return []
  }
}

function partitionFromBands(bands: readonly OrderPortionBandCode[]): OrderBandPartition {
  return bands.length > 0 ? { kind: 'BANDS', bands: [...bands] } : { kind: 'SINGLE' }
}

/**
 * The shipped partition when the org's programs are unknown (client
 * fallback for older payloads, orgs with no recognized program): a
 * pattern-bound org reads the NSLP / SBP defaults, anything else orders the
 * single column.
 */
export function orderBandPartition(input: {
  /** True when the org opts into any federal pattern program. */
  patternBound: boolean
  mealtime: string | null | undefined
}): OrderBandPartition {
  if (!input.patternBound) {
    return { kind: 'SINGLE' }
  }
  return partitionFromBands(defaultOrderBandsForProgram('NSLP', orderBandStyleForMealtime(input.mealtime)))
}

/**
 * The NARROW band(s) one menu grade tag maps to (before partition folding).
 * Legacy `k-12` rows span both K-12 patterns; infant/adult/unknown → none.
 */
export function orderPortionBandsForMenuCode(code: string | null | undefined): OrderPortionBandCode[] {
  if (!code || code.trim() === '') {
    return []
  }
  switch (normalizeGradeRangeCode(code)) {
    case 'pk':
    case 'age-1-2':
    case 'pk3-5':
      return ['pk']
    case 'k-5':
    case '6-8':
    case 'k-8':
      return ['k-8']
    case '9-12':
      return ['9-12']
    case 'k-12':
      return ['k-8', '9-12']
    default:
      return []
  }
}

/**
 * The partition bands a menu's tag set reaches: narrow bands fold into the
 * partition's coarser bands (k-8 / 9-12 → k-12 where the partition offers
 * k-12). An UNTAGGED menu (empty codes) spans every partition band —
 * mirroring `menuAllowsGradeRangeCode`'s untagged-matches-all rule, so the
 * columns and the cell gate can never disagree.
 */
export function orderPortionBandsForMenuCodes(
  codes: readonly string[] | null | undefined,
  partitionBands: readonly OrderPortionBandCode[],
): OrderPortionBandCode[] {
  const tags = (codes ?? []).filter((code) => code.trim() !== '')
  if (tags.length === 0) {
    return [...partitionBands]
  }
  const narrow = new Set(tags.flatMap((code) => orderPortionBandsForMenuCode(code)))
  const offersK12 = partitionBands.includes('k-12')
  // Childcare partitions (Infants | Age 1–5) only: infant tags reach Infants,
  // and PK / age 1–2 / age 3–5 tags reach Age 1–5. K-12 partitions never
  // offer these bands, so their columns are unchanged.
  const offersAge15 = partitionBands.includes('age-1-5')
  const reached = new Set<OrderPortionBandCode>()
  for (const code of tags.map((tag) => normalizeGradeRangeCode(tag))) {
    if (code === 'infant' && partitionBands.includes('infant')) {
      reached.add('infant')
    } else if (offersAge15 && (code === 'age-3-5' || code === 'age-1-2' || code === 'pk3-5' || code === 'pk')) {
      reached.add('age-1-5')
    }
  }
  for (const band of narrow) {
    if ((band === 'k-8' || band === '9-12') && offersK12 && !partitionBands.includes(band)) {
      reached.add('k-12')
    } else if (band === 'pk' && offersAge15 && !partitionBands.includes('pk')) {
      reached.add('age-1-5')
    } else {
      reached.add(band)
    }
  }
  return partitionBands.filter((band) => reached.has(band))
}

// ---------------------------------------------------------------------------
// Grade Mapping config (Menus -> Maintenance): per-PROGRAM partition
// OVERRIDES. Rows override the shipped defaults above — a program without a
// row uses `defaultOrderBandsForProgram`, so an empty config set is exactly
// Ordo's defaults. A row stores one band list per mealtime setting; an empty
// list is that mealtime's single column.
// ---------------------------------------------------------------------------

/** One stored per-program override row (OrderBandProgramConfig). */
export interface OrderBandProgramConfigRow {
  /** K12 / CHILDCARE / ADULT; rows written before audiences existed are K12. */
  audience?: string | null
  program: string
  singleColumn: boolean
  breakfastBands: readonly string[]
  lunchBands: readonly string[]
  snackBands: readonly string[]
  supperBands: readonly string[]
}

/** The stored band list a config row holds for one mealtime setting. */
export function orderBandRowBands(row: OrderBandProgramConfigRow, style: OrderBandMealtimeStyle): readonly string[] {
  switch (style) {
    case 'breakfast':
      return row.breakfastBands
    case 'lunch':
      return row.lunchBands
    case 'snack':
      return row.snackBands
    case 'supper':
      return row.supperBands
  }
}

function sanitizeBands(bands: readonly string[]): OrderPortionBandCode[] {
  const cleaned = new Set(bands.map((band) => normalizeGradeRangeCode(band)))
  return ORDER_PORTION_BAND_CODES.filter((band) => cleaned.has(band))
}

/**
 * The partition ONE program contributes: its override row when present,
 * else its shipped default. Unknown programs (not in ORDER_BAND_PROGRAMS)
 * contribute nothing. An override with an EMPTY band list for the style is
 * treated as single-column for that style — never "no columns".
 */
function programPartition(
  program: string,
  style: OrderBandMealtimeStyle,
  rowByProgram: ReadonlyMap<string, OrderBandProgramConfigRow>,
  audience: Audience,
): OrderBandPartition | null {
  const normalized = program.trim().toUpperCase()
  if (!(ORDER_BAND_PROGRAMS as readonly string[]).includes(normalized)) {
    return null
  }
  const row = rowByProgram.get(normalized)
  if (row) {
    return row.singleColumn ? { kind: 'SINGLE' } : partitionFromBands(sanitizeBands(orderBandRowBands(row, style)))
  }
  return partitionFromBands(defaultOrderBandsForProgram(normalized, style, audience))
}

/**
 * The order-sheet partition for an org, resolved through the Grade Mapping
 * config. Multi-program orgs union their programs' band partitions (any
 * banded program wins over a single-column one — the federal pattern must
 * stay separable); an org whose programs all resolve single-column gets
 * the single column; an org with NO recognized programs falls back to the
 * shipped rule (not pattern-bound → single column).
 */
export function resolveOrderBandPartition(input: {
  configs: ReadonlyArray<OrderBandProgramConfigRow>
  programs: readonly string[] | null | undefined
  mealtime: string | null | undefined
  /** The school's audience (from its school type); K12 when omitted. */
  audience?: Audience
}): OrderBandPartition {
  const audience = input.audience ?? 'K12'
  const style = orderBandStyleForMealtime(input.mealtime)
  const rowByProgram = new Map(
    input.configs
      .filter((row) => (row.audience ?? 'K12') === audience)
      .map((row) => [row.program.trim().toUpperCase(), row]),
  )
  const partitions = (input.programs ?? [])
    .map((program) => programPartition(program, style, rowByProgram, audience))
    .filter((partition): partition is OrderBandPartition => partition !== null)
  if (partitions.length === 0 && audience !== 'K12') {
    // No recognized program: the audience's own default still applies.
    return partitionFromBands(defaultOrderBandsForProgram(null, style, audience))
  }
  if (partitions.length === 0) {
    return orderBandPartition({ patternBound: isOrderPatternBound(input.programs), mealtime: input.mealtime })
  }
  const bands = new Set(partitions.flatMap((partition) => (partition.kind === 'BANDS' ? partition.bands : [])))
  return bands.size > 0
    ? { kind: 'BANDS', bands: ORDER_PORTION_BAND_CODES.filter((band) => bands.has(band)) }
    : { kind: 'SINGLE' }
}

// ---------------------------------------------------------------------------
// BAND COLUMN FOLD — resolving per-column config authored against the CAMPUS
// grade-range columns for a menu-derived BAND column.
//
// The order sheet identifies an AGE_GROUP column by the canonical BAND
// GradeRange id (`buildMenuOrderColumns`), but every per-column setting a
// school has ever authored — milk shares, component delivery rates — is
// stored keyed by the campus `LocationGradeRange` row id that the SETTINGS
// grid renders (`buildOrderColumns`). Nothing resolved after the band cutover
// (PRs #2089/#2112) because the two key spaces don't overlap: prod carried 107
// milk-share rows and 84 delivery-rate rows keyed to LocationGradeRange ids
// and ZERO keyed to a band id (measured 2026-08-25).
//
// The fix is read-side: a band column ALSO accepts rows keyed to the campus
// columns that fold into it, at LOWER precedence than an exact band-keyed row.
// This function is the one place that decides which campus columns those are,
// and in what order — so the client order sheet and the server resolvers can
// never disagree.
// ---------------------------------------------------------------------------

/** One campus grade-range column, as the fold sees it. */
export interface BandFoldColumn {
  /** The `LocationGradeRange` row id — the value stored in `group_value`. */
  columnId: string
  /** The row's canonical portion-tier code ('6-8', 'age-3-5', 'k-5', …). */
  gradeRangeCode: string | null | undefined
  /** The campus's own display order for the column. Absent sorts last. */
  sortOrder?: number | null
}

/** True when a campus tier's portion band folds into the given band column. */
/** Grade codes the childcare Age 1–5 band covers. */
const AGE_1_5_CODES: readonly string[] = Object.freeze(['age-1-2', 'age-3-5', 'pk3-5', 'pk'])

/**
 * Whether a campus grade-range code orders through a band column. `k-12` is
 * the collapsed partition (both K-8 and 9-12 tiers); the childcare bands take
 * infant tiers (Infant) and the age 1–2 / 3–5 / preschool tiers (Age 1–5).
 */
export function bandCoversGradeRangeCode(
  band: OrderPortionBandCode,
  gradeRangeCode: string | null | undefined,
): boolean {
  if (!gradeRangeCode) {
    return false
  }
  if (band === 'infant') {
    return normalizeGradeRangeCode(gradeRangeCode) === 'infant'
  }
  if (band === 'age-1-5') {
    return AGE_1_5_CODES.includes(normalizeGradeRangeCode(gradeRangeCode))
  }
  const narrow = orderPortionBandsForMenuCode(gradeRangeCode)
  return band === 'k-12' ? narrow.some((b) => b === 'k-8' || b === '9-12') : narrow.includes(band)
}

function foldsIntoBand(gradeRangeCode: string | null | undefined, band: OrderPortionBandCode): boolean {
  return bandCoversGradeRangeCode(band, gradeRangeCode)
}

/**
 * The campus `LocationGradeRange` column ids whose stored per-column config a
 * BAND column should fall back to, MOST PREFERRED FIRST.
 *
 * `band` is the band the column renders, or `null` for the band-less columns
 * (the PRIVATE `ALL_GRADES` column and the NONE whole-school column) — every
 * campus column folds into a band-less column, because it spans them all.
 *
 * ## Tie-break — the campus's own leftmost column wins
 *
 * When several campus columns fold into one band (Howard Street's `k-5` and
 * `6-8` both fold into `k-8`), their stored values may disagree and there is
 * no way to combine two distributions without inventing a number — summing
 * milk shares would double the district's percentages. So exactly ONE campus
 * column supplies the fallback, chosen deterministically:
 *
 *   1. lowest `sortOrder` (the campus's own display order — the column the
 *      school sees FIRST in its settings grid),
 *   2. then the grade-range code, alphabetically,
 *   3. then the row id.
 *
 * Every candidate is returned in that order so a caller can fall through to
 * the next one when the winner has no stored row for the cell being resolved;
 * what the order guarantees is that the CHOICE never depends on database row
 * order or on which campus column was edited last.
 */
export function bandFoldColumnIds(band: OrderPortionBandCode | null, columns: readonly BandFoldColumn[]): string[] {
  return columns
    .filter((column) => band === null || foldsIntoBand(column.gradeRangeCode, band))
    .map((column, index) => ({ column, index }))
    .sort(
      (a, b) =>
        (a.column.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.column.sortOrder ?? Number.MAX_SAFE_INTEGER) ||
        (a.column.gradeRangeCode ?? '').localeCompare(b.column.gradeRangeCode ?? '') ||
        a.column.columnId.localeCompare(b.column.columnId) ||
        a.index - b.index,
    )
    .map((entry) => entry.column.columnId)
}

/** Narrow an arbitrary grade-range code to a band column code, or null. */
export function asOrderPortionBandCode(code: string | null | undefined): OrderPortionBandCode | null {
  if (!code) {
    return null
  }
  const normalized = normalizeGradeRangeCode(code)
  return (ORDER_PORTION_BAND_CODES as readonly string[]).includes(normalized)
    ? (normalized as OrderPortionBandCode)
    : null
}
